import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import Table from '@shared/components/table/Table';
import Pagination from '@shared/components/pagination/Pagination';
import { Input } from '@shared/components/common/Input';
import { useStore } from '@shared/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { formatDateTime } from '@shared/utils/datetime';
import { DATE_YMD } from '@shared/components/constants/common.constants';
import { formatPublicationTitle } from '@shared/utils/publication';
import NotFoundPage from '@shared/components/common/NotFoundPage';
import { GENDER_CONFIG } from '@/shared/components/constants/select.constant';
import { Gender } from '@/shared/types/enums';
import debounce from 'lodash.debounce';
import Tooltip from '@shared/components/common/Tooltip';

const DetailRow = ({ label, value, showTooltip = false }: { label: string, value: string | null | undefined, showTooltip?: boolean }) => (
    <div className='flex flex-col md:flex-row gap-8'>
        <div className='w-full md:w-1/3 min-w-[160px] text-[#999999] mb-1 md:mb-0'>
            {label}
        </div>
        <div className='flex-1'>
            {showTooltip ?
                (
                    <Tooltip
                        content={value || '--'}
                        triggerAsChild
                        triggerClassName='w-fit'
                        contentClassName='max-w-[320px] whitespace-pre-line text-break'
                    >
                        <div className='line-clamp-2'>{value || '--'}</div>
                    </Tooltip>
                ) :
                (
                    value || '--'
                )}
        </div>
    </div>
);

const UserDetailPage = observer(() => {
    const { id } = useParams();
    const { groupAdminStore } = useStore();
    const {
        userDetail,
        userPublications,
        totalUserPublications,
        loading
    } = groupAdminStore;

    // Search State - separate input value from debounced search term
    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Debounced search function to avoid excessive API calls
    const debouncedSetSearchTerm = useCallback(
        debounce((value: string) => {
            setSearchTerm(value);
            setCurrentPage(1);
        }, 500),
        []
    );

    // Handle input change - update inputValue immediately, but debounce the search term
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        debouncedSetSearchTerm(value.trim());
    };

    useEffect(() => {
        if (id) {
            groupAdminStore.fetchUserDetail(id);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            groupAdminStore.fetchUserPublications(id, {
                page: currentPage,
                size: pageSize,
                search: searchTerm
            });
        }
    }, [id, currentPage, searchTerm]);

    const columns: ColumnDef<any>[] = useMemo(() => [
        {
            accessorKey: 'no',
            header: 'No.',
            cell: ({ row }) => (currentPage - 1) * pageSize + row.index + 1,
            size: 60,
            enableSorting: false
        },
        {
            accessorKey: 'code',
            header: '商品コード',
            size: 160,
            cell: ({ row }) => (
                <Tooltip content={row.original.code} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                    <div className='line-clamp-2'>{row.original.code}</div>
                </Tooltip>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'title',
            header: '商品名',
            cell: ({ row }) => (
                <Tooltip content={formatPublicationTitle(row.original)} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                    <div className='line-clamp-2'>{formatPublicationTitle(row.original)}</div>
                </Tooltip>
            ),
            size: 240,
            enableSorting: false
        },
        {
            accessorKey: 'type',
            header: 'プラン種別',
            cell: ({ row }) => {
                const packageType = row.original.packageType;
                if (!packageType) return '--';
                if (packageType === 'UNLIMITED') {
                    return '読み放題';
                }
                return '指定書籍';
            },
            enableSorting: false
        },
        {
            accessorKey: 'startDate',
            header: '開始日',
            cell: ({ row }) => formatDateTime(row.original.startDate, DATE_YMD, { returnDefault: '--' }),
            enableSorting: false
        },
        {
            accessorKey: 'expirationDate',
            header: '終了日',
            cell: ({ row }) => {
                const packageType = row.original.packageType;
                const expirationDate = row.original.expirationDate;
                if (packageType && expirationDate === '2999-12-31') return '無制限';
                return formatDateTime(expirationDate, DATE_YMD, { returnDefault: '--' });
            },
            enableSorting: false
        }
    ], [currentPage]);

    if (!userDetail && !loading) {
        return (
            <NotFoundPage />
        );
    }

    return (
        <section className='py-6 space-y-6'>
            {/* Page Title */}
            <h1 className='text-[30px] leading-[36px] font-semibold mb-4'>
                ユーザー詳細
            </h1>

            {/* User Info Card */}
            <div className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] p-4 md:p-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6'>
                    <DetailRow label='ユーザーID' value={userDetail?.userId} />
                    <DetailRow label='氏名' value={userDetail?.fullName} showTooltip />
                    <DetailRow label='氏名（フリガナ）' value={userDetail?.fullNameFuri} showTooltip />
                    <DetailRow label='性別' value={GENDER_CONFIG[userDetail?.gender as Gender]?.label || ''} />
                    <DetailRow label='生年月日' value={formatDateTime(userDetail?.birthday, DATE_YMD, { returnDefault: '--' })} />
                    <DetailRow label='メールアドレス' value={userDetail?.email} showTooltip />
                    <DetailRow label='薬剤師名簿登録番号' value={userDetail?.pharmacistNumber} />
                    <DetailRow label='電話番号' value={userDetail?.phoneNumber} />
                    <DetailRow label='住所' value={`${userDetail?.address1 || ''} ${userDetail?.address2 || ''}`} showTooltip />
                    <DetailRow label='法人名' value={userDetail?.companyName} showTooltip />
                </div>
            </div>

            {/* Bookshelf Card */}
            <div className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] p-4 md:p-6'>
                <h2 className='text-[20px] font-semibold mb-6'>本棚一覧</h2>
                {/* Search */}
                <div className='flex items-center gap-4 mb-6'>
                    <label className='font-bold min-w-fit'>検索</label>
                    <Input
                        placeholder='コード / 名前で検索'
                        className='px-4 rounded-none h-[56px] border border-[#E7E7E7] max-w-[404px]'
                        value={inputValue}
                        onChange={handleSearchChange}
                    />
                </div>

                {/* Table */}
                <Table
                    columns={columns}
                    data={userPublications}
                    showTableFooter={false}
                    showSelectPageSize={false}
                    loadingData={loading}
                    noData={<p className='text-[#999999]'>該当するデータが見つかりませんでした。</p>}
                    headerClassName='bg-[#F2F2F2] text-[#6F767E] font-medium'
                />

                <div className='flex justify-center py-4 border-t border-gray-200 mt-4'>
                    <Pagination
                        type='table-pagination'
                        paginationClassName='w-fit'
                        currentPage={currentPage}
                        totalRecords={totalUserPublications}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </div>
        </section>
    );
});

export default UserDetailPage;
